import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  useQuery,
  useQueryClient,
  useMutation,
  MutateOptions,
} from "react-query";
import axios from "axios";

import api from "@api/index";
import { IResponseDataDto } from "@interfaces/rest/response.dto";
import { IGuestDto } from "@interfaces/rest/guestData.dto";
import { IMediaDto } from "@interfaces/rest/media.dto";
import { ISurveyAnswer } from "@pages/Survey/survey.types";
import { QueryKeyEnum } from "@interfaces/enums/queryKeys.enum";
import { SITE_SETTINGS } from "@config/constants";
import useConfig from "@providers/configProvider/useConfig";
import { IGuestbookFormValues } from "@components/guestbookForm/guestbookForm.types";
import {
  IIdentifierParams,
  ISaveGuestWithVisitParams,
  ISaveUserParams,
} from "./user.types";

const useUserState = (
  providedBarcode?: string,
  providedPhoneNumber?: string
) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { config } = useConfig();

  const initializeIdentifier = useCallback(
    (providedValue: string | undefined, storageKey: string): string => {
      if (providedValue) {
        if (!config?.isKiosk) {
          localStorage.setItem(storageKey, providedValue);
        }
        return providedValue;
      }
      return config?.isKiosk ? "" : localStorage.getItem(storageKey) ?? "";
    },
    [config?.isKiosk]
  );

  const [user, setUser] = useState<IGuestDto | null>(null);
  const [activeBarcode, setActiveBarcode] = useState(() =>
    initializeIdentifier(providedBarcode, "activeBarcode")
  );
  const [activePhoneNumber, setActivePhoneNumber] = useState(() =>
    initializeIdentifier(providedPhoneNumber, "activePhoneNumber")
  );
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [isInterviewing, setIsInterviewing] = useState(false);

  const updateIdentifier = useCallback(
    (value: string, setter: (value: string) => void, storageKey: string) => {
      setter(value);
      if (!config?.isKiosk) {
        localStorage.setItem(storageKey, value);
      }
    },
    [config?.isKiosk]
  );

  const clearIdentifier = useCallback(
    (setter: (value: string) => void, storageKey: string) => {
      setter("");
      if (!config?.isKiosk) {
        localStorage.removeItem(storageKey);
      }
    },
    [config?.isKiosk]
  );

  const createNavigationUrl = useCallback(
    (path: string, barcode?: string, phoneNumber?: string) => {
      const params = new URLSearchParams();
      if (barcode) {
        params.append("barcode", barcode);
      }
      if (phoneNumber) {
        params.append("phoneNumber", phoneNumber);
      }
      const queryString = params.toString();
      return `${path}${queryString ? `?${queryString}` : ""}`;
    },
    []
  );

  const getUserData = useCallback(
    async ({ barcode, phoneNumber }: IIdentifierParams) => {
      const params: Record<string, string> = {};
      if (barcode) {
        params.ticketBarcode = barcode;
      }
      if (phoneNumber) {
        params.phoneNumber = phoneNumber;
      }
      const response = await axios.get(api.guestInfo, { params });
      return response.data;
    },
    []
  );

  const { refetch: refetchUserData, isFetching: isUserFetching } = useQuery(
    [QueryKeyEnum.getUserData, activeBarcode, activePhoneNumber],
    () =>
      getUserData({ barcode: activeBarcode, phoneNumber: activePhoneNumber }),
    {
      enabled: false,
      cacheTime: 0,
      onSuccess: (response) => {
        if (!response.IsSuccess && !response.data) {
          navigate(
            createNavigationUrl("/login", activeBarcode, activePhoneNumber)
          );
          return;
        }
        setUser(response.data);
        navigate("/photos");
      },
    }
  );

  const saveUserMutation = useMutation(
    async ({
      ticketBarcodes,
      phoneNumber,
      email,
      zipCode,
      captureNewsYN,
    }: ISaveUserParams) => {
      const response = await axios.post<IResponseDataDto<string>>(
        api.saveGuest,
        {
          data: {
            email,
            zipCode,
            captureNewsYN,
          },
          ticketBarcodes,
          phoneNumber,
          survey: {},
        }
      );

      if (!response.data?.isSuccess) {
        const message =
          response.data?.errorMessage ?? "Failed to save. Please try again.";
        throw new Error(message);
      }

      return response.data.data;
    },
    {
      onError: (error) => {
        console.error("Save user failed:", error);
      },
    }
  );

  const visitFormMutation = useMutation(
    async (values: IGuestbookFormValues) => {
      const response = await axios.post<IResponseDataDto<{ id: string }>>(
        api.visitFormSubmit(values.isGuestbookAnimation),
        {
          firstName: values.firstName,
          lastName: values.lastName,
          postalCode: values.zipCode,
          reason: values.reason,
          lat: values.location?.lat,
          lng: values.location?.lng,
          city: values.location?.city,
          state: values.location?.state,
          country: values.country,
          countryAbbr: values.location?.countryAbbr,
          placeId: values.location?.placeId,
          geocodeSource: "google",
          barcode: activeBarcode || null,
          phoneNumber: activePhoneNumber || null,
        }
      );

      if (!response.data?.isSuccess) {
        const message =
          response.data?.errorMessage ?? "Failed to submit. Please try again.";
        throw new Error(message);
      }

      return response.data.data;
    },
    {
      onSuccess: (data) => {
        if (!data?.id) {
          return;
        }

        setUser((prevUser) =>
          prevUser ? { ...prevUser, guestVisitDataId: data.id } : prevUser
        );
      },
    }
  );

  const saveGuestWithVisitMutation = useMutation(
    async ({
      ticketBarcodes,
      phoneNumber,
      email,
      zipCode,
      captureNewsYN,
      firstName,
      lastName,
      country,
      reason,
      location,
      isGuestbookAnimation,
    }: ISaveGuestWithVisitParams) => {
      const response = await axios.post<
        IResponseDataDto<{ guestVisitDataId: string }>
      >(api.saveGuestWithVisit(isGuestbookAnimation), {
        data: {
          email,
          zipCode,
          captureNewsYN,
        },
        ticketBarcodes,
        phoneNumber,
        survey: {},
        firstName,
        lastName,
        postalCode: zipCode,
        reason,
        lat: location?.lat,
        lng: location?.lng,
        city: location?.city,
        state: location?.state,
        country,
        countryAbbr: location?.countryAbbr,
        placeId: location?.placeId,
        geocodeSource: "google",
      });

      if (!response.data?.isSuccess) {
        const message =
          response.data?.errorMessage ?? "Failed to submit. Please try again.";
        throw new Error(message);
      }

      return response.data.data;
    },
    {
      onError: (error) => {
        console.error("Save guest with visit failed:", error);
      },
    }
  );

  const animationTriggerMutation = useMutation(async (id: string) => {
    const response = await axios.post(api.visitAnimationTrigger, {
      id,
    });

    if (!response.data?.isSuccess) {
      const message =
        response.data?.errorMessage ??
        "Failed to trigger animation. Please try again.";
      throw new Error(message);
    }
  });

  const {
    data: photos,
    isLoading: isLoadingImages,
    refetch: refetchPhotos,
  } = useQuery(
    [QueryKeyEnum.getUserPhotos, activeBarcode, activePhoneNumber],
    async () => {
      const params: Record<string, string> = {};
      if (activeBarcode) {
        params.ticketBarcode = activeBarcode;
      }
      if (activePhoneNumber) {
        params.phoneNumber = activePhoneNumber;
      }
      const response = await axios.get<IResponseDataDto<IMediaDto[]>>(
        api.getMedia,
        { params }
      );
      return response.data.data ?? [];
    },
    {
      enabled: Boolean(activeBarcode || activePhoneNumber),
      refetchInterval: 10000,
      onError: (error) => {
        console.error("Error getting photos by barcode:", error);
      },
    }
  );

  const updatePhotosCache = useCallback(
    (updater: (prev: IMediaDto[] | undefined) => IMediaDto[] | undefined) => {
      queryClient.setQueryData<IMediaDto[] | undefined>(
        [QueryKeyEnum.getUserPhotos, activeBarcode, activePhoneNumber],
        updater
      );
    },
    [queryClient, activeBarcode, activePhoneNumber]
  );

  const handleSurveySubmit = useCallback(
    async (answersList: ISurveyAnswer): Promise<void> => {
      try {
        const questionIds = Object.keys(answersList);
        const answersDto = questionIds.map((questionId) => ({
          questionId,
          answer: answersList[questionId],
        }));

        await axios.post(api.postSurveyAnswer, {
          answers: answersDto,
          guestId: user?.guestId,
          ticketBarcode: activeBarcode || user?.guestData.ticketBarcodes[0],
          phoneNumber: activePhoneNumber || user?.guestData.phoneNumber,
        });

        setIsInterviewing(false);
        setUser((prevUser) =>
          prevUser
            ? {
                ...prevUser,
                guestData: {
                  ...prevUser.guestData,
                  passedSurvey: true,
                },
              }
            : null
        );
      } catch (error) {
        console.error("Error submiting survey:", error);
      }
    },
    [
      user?.guestId,
      user?.guestData.ticketBarcodes,
      user?.guestData.phoneNumber,
      activeBarcode,
      activePhoneNumber,
    ]
  );

  const share = useCallback(
    async (id: string | string[], email?: string): Promise<boolean> => {
      try {
        const payload = {
          from: user?.guestData.email,
          mediaIds: Array.isArray(id) ? id : [id],
          ...(email ? { emailsList: [email] } : {}),
        };

        const response = await axios.post(api.mediaShare, payload);
        setSelectedPhotos([]);
        return response.data.IsSuccess;
      } catch (error: any) {
        return false;
      }
    },
    [user?.guestData.email]
  );

  const downloadMedia = async (
    mediaPath: string,
    captureType: string
  ): Promise<void> => {
    try {
      const response = await axios.get(api.mediaDownload, {
        params: {
          mediaPath,
          captureType,
          email: user?.guestData.email,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      const filename = mediaPath.split("/").pop() || "download";

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading media:", error);
      throw error;
    }
  };

  const saveSiverScreenBg = useCallback(
    async (mediaId: string, backgroundId: string): Promise<boolean> => {
      try {
        await axios.post(api.mediaCompose(mediaId), {
          backgroundMediaLayer: backgroundId,
        });
        return true;
      } catch {
        return false;
      }
    },
    []
  );

  const resetUser = useCallback(() => {
    setUser(null);
    clearIdentifier(setActiveBarcode, "activeBarcode");
    clearIdentifier(setActivePhoneNumber, "activePhoneNumber");
    setSelectedPhotos([]);
  }, [clearIdentifier]);

  const checkIsShouldSurvey = useCallback(() => {
    if (user?.guestData && !user.guestData.passedSurvey) {
      setIsInterviewing(true);
    }
  }, [user?.guestData]);

  const handleSurveyClose = useCallback(() => {
    setIsInterviewing(false);
  }, []);

  const getUser = useCallback(
    async ({ barcode, phoneNumber }: IIdentifierParams) => {
      if (barcode) {
        updateIdentifier(barcode, setActiveBarcode, "activeBarcode");
      }
      if (phoneNumber) {
        updateIdentifier(
          phoneNumber,
          setActivePhoneNumber,
          "activePhoneNumber"
        );
      }

      try {
        const response = await getUserData({ barcode, phoneNumber });
        if (!response.IsSuccess && !response.data) {
          navigate(createNavigationUrl("/login", barcode, phoneNumber));
          return;
        }
        setUser(response.data);
        navigate("/photos");
      } catch (error) {
        console.error("Error getting user by barcode:", error);
        navigate(createNavigationUrl("/login", barcode, phoneNumber));
      }
    },
    [updateIdentifier, getUserData, navigate, createNavigationUrl]
  );

  const saveUser = useCallback(
    async (
      {
        ticketBarcodes,
        phoneNumber,
        email,
        zipCode,
        captureNewsYN,
      }: ISaveUserParams,
      options?: MutateOptions<unknown, unknown, ISaveUserParams>
    ) => {
      if (ticketBarcodes) {
        setActiveBarcode(ticketBarcodes);
      }
      if (phoneNumber) {
        setActivePhoneNumber(phoneNumber);
      }

      saveUserMutation.mutate(
        { ticketBarcodes, phoneNumber, email, zipCode, captureNewsYN },
        options
      );
    },
    [saveUserMutation]
  );

  const saveGuestWithVisit = useCallback(
    (
      params: ISaveGuestWithVisitParams,
      options?: MutateOptions<
        { guestVisitDataId: string } | null,
        unknown,
        ISaveGuestWithVisitParams
      >
    ) => {
      if (params.ticketBarcodes) {
        setActiveBarcode(params.ticketBarcodes);
      }
      if (params.phoneNumber) {
        setActivePhoneNumber(params.phoneNumber);
      }

      saveGuestWithVisitMutation.mutate(params, options);
    },
    [saveGuestWithVisitMutation]
  );

  const selectPhoto = useCallback((id: string, isSelecting: boolean) => {
    setSelectedPhotos((prevList) =>
      isSelecting
        ? [...prevList, id]
        : prevList.filter((listItemId) => listItemId !== id)
    );
  }, []);

  const selectAllPhotos = useCallback(() => {
    if (photos) {
      setSelectedPhotos(photos.map((photo) => photo.mediaId));
    }
  }, [photos]);

  const selectSinglePhoto = useCallback(
    (id: string) => setSelectedPhotos([id]),
    []
  );

  const saveAdjustments = useCallback(
    async (
      mediaId: string,
      adjustments: { exposure: number; blackLevel: number }
    ): Promise<boolean> => {
      try {
        const response = await axios.post(
          api.mediaAdjust(mediaId),
          adjustments
        );
        const isSuccess: boolean = response.data?.data ?? false;

        if (isSuccess) {
          await new Promise<void>((resolve) => {
            setTimeout(resolve, 3000);
          });
          await refetchPhotos();
        }

        return isSuccess;
      } catch (error) {
        console.error("Error saving adjustments:", error);
        return false;
      }
    },
    [refetchPhotos]
  );

  const clearSelectedPhotos = useCallback(() => setSelectedPhotos([]), []);

  const markPhotoAsUsed = useCallback(
    (mediaId: string, mediaLayerId: string) => {
      updatePhotosCache((prev) => {
        if (!prev) {
          return prev;
        }

        try {
          const itemIndex = prev.findIndex((item) => item.mediaId === mediaId);
          if (itemIndex === -1) {
            return prev;
          }

          const item = prev[itemIndex];
          if (!item.mediaLayers) {
            return prev;
          }

          const itemLayerIndex = item.mediaLayers.findIndex(
            (layer) => layer.mediaLayerId === mediaLayerId
          );
          if (itemLayerIndex === -1) {
            return prev;
          }

          const updatedLayers = [...item.mediaLayers];
          updatedLayers[itemLayerIndex] = {
            ...updatedLayers[itemLayerIndex],
            isUsed: true,
          };

          const updatedPhotos = [...prev];
          updatedPhotos[itemIndex] = {
            ...item,
            mediaLayers: updatedLayers,
          };

          return updatedPhotos;
        } catch (error) {
          console.error("Error marking photo as used:", error);
          return prev;
        }
      });
    },
    [updatePhotosCache]
  );

  useEffect(() => {
    if (
      providedBarcode?.length === SITE_SETTINGS.BARCODE_LENGTH ||
      providedPhoneNumber?.length
    ) {
      refetchUserData();
    }
  }, [providedBarcode, providedPhoneNumber, refetchUserData]);

  return useMemo(
    () => ({
      isLoading:
        saveUserMutation.isLoading ||
        saveGuestWithVisitMutation.isLoading ||
        isUserFetching,
      submitVisitForm: visitFormMutation.mutate,
      saveGuestWithVisit,
      triggerAnimation: animationTriggerMutation.mutate,
      isLoadingImages,
      user,
      activeBarcode,
      activePhoneNumber,
      photos,
      selectedPhotos,
      getUser,
      saveUser,
      refetchUserData,
      resetUser,
      selectPhoto,
      selectSinglePhoto,
      selectAllPhotos,
      clearSelectedPhotos,
      markPhotoAsUsed,
      share,
      saveAdjustments,
      saveSiverScreenBg,
      isInterviewing,
      checkIsShouldSurvey,
      handleSurveyClose,
      handleSurveySubmit,
      downloadMedia,
      refetchPhotos,
    }),
    [
      saveUserMutation.isLoading,
      saveGuestWithVisitMutation.isLoading,
      saveGuestWithVisit,
      visitFormMutation.mutate,
      animationTriggerMutation.mutate,
      isUserFetching,
      isLoadingImages,
      user,
      activeBarcode,
      activePhoneNumber,
      photos,
      selectedPhotos,
      getUser,
      saveUser,
      refetchUserData,
      resetUser,
      selectPhoto,
      selectSinglePhoto,
      selectAllPhotos,
      clearSelectedPhotos,
      markPhotoAsUsed,
      share,
      saveAdjustments,
      saveSiverScreenBg,
      isInterviewing,
      checkIsShouldSurvey,
      handleSurveyClose,
      handleSurveySubmit,
      refetchPhotos,
    ]
  );
};

export default useUserState;
